import { reactive, readonly } from 'vue';

// Tiny app-wide toast queue. Anything in the app can call `toast.error(err)`
// or `toast.success(msg)`; <toast-host> watches the reactive `toasts` list and
// renders them. Auto-dismiss timing lives on each entry so the host doesn't
// have to track timers.

const DEFAULT_DURATION_MS = 6_000;
const ERROR_DURATION_MS = 9_000;

const state = reactive({
    toasts: [],
});

let nextId = 1;

function push(toast) {
    const entry = {
        id: nextId++,
        title: toast.title,
        message: toast.message,
        variant: toast.variant || 'info',
        code: toast.code || null,
        errors: toast.errors || [],
        traceId: toast.traceId || null,
        duration: toast.duration || DEFAULT_DURATION_MS,
    };
    state.toasts.push(entry);
    if (entry.duration > 0) {
        setTimeout(() => dismiss(entry.id), entry.duration);
    }
    return entry.id;
}

function dismiss(id) {
    const idx = state.toasts.findIndex((t) => t.id === id);
    if (idx !== -1) state.toasts.splice(idx, 1);
}

// Accepts either an Error built by api-client.buildApiError (has `code`,
// `detail`, `errors`, `traceId`) or a plain Error / string. Always shows
// something useful even when the backend didn't speak problem+json.
function error(err, fallbackTitle = 'Something went wrong') {
    if (typeof err === 'string') {
        return push({ title: fallbackTitle, message: err, variant: 'error' });
    }
    const message = err?.detail || err?.message || 'Unknown error';
    const title = err?.title || fallbackTitle;
    return push({
        title,
        message,
        variant: 'error',
        code: err?.code,
        errors: err?.errors,
        traceId: err?.traceId,
        duration: ERROR_DURATION_MS,
    });
}

function success(message, title = 'Done') {
    return push({ title, message, variant: 'success' });
}

function info(message, title = '') {
    return push({ title, message, variant: 'info' });
}

export default {
    toasts: readonly(state.toasts),
    error,
    success,
    info,
    dismiss,
};
